import { track } from "~/modules/analytics/track";

// // // //

/**
 * DashboardPageUnlocksUserEvents
 * Registry of events for the dashboard page unlock drawers + boundary
 */
export enum DashboardPageUnlocksUserEvents {
    // LoginToUnlockDrawer
    LoginToUnlockDrawerGateViewed = "Dashboard Page Unlocks - Login To Unlock Drawer - Gate Viewed",
    LoginToUnlockDrawerShown = "Dashboard Page Unlocks - Login To Unlock Drawer - Shown",
    LoginToUnlockDrawerCreateAccountClick = "Dashboard Page Unlocks - Login To Unlock Drawer - Create Account Click",
    LoginToUnlockDrawerSignInClick = "Dashboard Page Unlocks - Login To Unlock Drawer - Sign In Click",
    LoginToUnlockDrawerChangeAuthVariant = "Dashboard Page Unlocks - Login To Unlock Drawer - Change Auth Variant",
    // UpgradeToUnlockDrawer
    UpgradeToUnlockDrawerGateViewed = "Dashboard Page Unlocks - Upgrade To Unlock Drawer - Gate Viewed",
    UpgradeToUnlockDrawerShown = "Dashboard Page Unlocks - Upgrade To Unlock Drawer - Shown",
    UpgradeToUnlockDrawerViewPricingLinkClick = "Dashboard Page Unlocks - Upgrade To Unlock Drawer - View Pricing Link Click",
    // UnlockPageDrawer
    UnlockPageDrawerGateViewed = "Dashboard Page Unlocks - Unlock Page Drawer - Gate Viewed",
    UnlockPageDrawerShown = "Dashboard Page Unlocks - Unlock Page Drawer - Shown",
    UnlockPageDrawerUnlockClick = "Dashboard Page Unlocks - Unlock Page Drawer - Unlock Click",
    UnlockPageDrawerViewPricingLinkClick = "Dashboard Page Unlocks - Unlock Page Drawer - View Pricing Link Click",
    // DashboardUnlockBoundary
    PageUnlockAttempted = "Dashboard Page Unlocks - Page Unlock Attempted",
    PageUnlockSuccess = "Dashboard Page Unlocks - Page Unlock Success",
    PageUnlockError = "Dashboard Page Unlocks - Page Unlock Error",
}

type AuthVariant = "login" | "register";

// // // // LoginToUnlockDrawer

/**
 * trackLoginToUnlockDrawerGateViewed
 * Tracks the event when the login gate is rendered for a user.
 * Unlike `Shown`, this does not depend on scroll position.
 */
export function trackLoginToUnlockDrawerGateViewed() {
    track(DashboardPageUnlocksUserEvents.LoginToUnlockDrawerGateViewed);
}

/**
 * trackLoginToUnlockDrawerShown
 * Tracks the event when the LoginToUnlockDrawer becomes visible
 */
export function trackLoginToUnlockDrawerShown() {
    track(DashboardPageUnlocksUserEvents.LoginToUnlockDrawerShown);
}

/**
 * trackLoginToUnlockDrawerCreateAccountClick
 * Tracks the event when a user clicks the "Create account" button
 */
export function trackLoginToUnlockDrawerCreateAccountClick() {
    track(DashboardPageUnlocksUserEvents.LoginToUnlockDrawerCreateAccountClick);
}

/**
 * trackLoginToUnlockDrawerSignInClick
 * Tracks the event when a user clicks "Sign in" before the auth form is open
 */
export function trackLoginToUnlockDrawerSignInClick() {
    track(DashboardPageUnlocksUserEvents.LoginToUnlockDrawerSignInClick);
}

/**
 * trackLoginToUnlockDrawerChangeAuthVariant
 * Tracks the event when a user swaps between login + register inside the open auth form
 * @param {AuthVariant} props.prevValue - The variant being swapped away from
 * @param {AuthVariant} props.nextValue - The variant being swapped to
 */
export function trackLoginToUnlockDrawerChangeAuthVariant(props: {
    prevValue: AuthVariant;
    nextValue: AuthVariant;
}) {
    track(
        DashboardPageUnlocksUserEvents.LoginToUnlockDrawerChangeAuthVariant,
        props,
    );
}

// // // // UpgradeToUnlockDrawer

/**
 * trackUpgradeToUnlockDrawerGateViewed
 * Tracks the event when the upgrade gate is rendered for a user.
 * Unlike `Shown`, this does not depend on scroll position.
 */
export function trackUpgradeToUnlockDrawerGateViewed(props: {
    organizationId: string;
}) {
    track(
        DashboardPageUnlocksUserEvents.UpgradeToUnlockDrawerGateViewed,
        props,
    );
}

/**
 * trackUpgradeToUnlockDrawerShown
 * Tracks the event when the UpgradeToUnlockDrawer becomes visible
 */
export function trackUpgradeToUnlockDrawerShown(props: {
    organizationId: string;
}) {
    track(DashboardPageUnlocksUserEvents.UpgradeToUnlockDrawerShown, props);
}

// // // // UnlockPageDrawer

/**
 * trackUnlockPageDrawerGateViewed
 * Tracks the event when the unlock gate is rendered for a user.
 * Unlike `Shown`, this does not depend on scroll position.
 */
export function trackUnlockPageDrawerGateViewed(props: {
    remainingUnlockCount?: number;
}) {
    track(DashboardPageUnlocksUserEvents.UnlockPageDrawerGateViewed, props);
}

/**
 * trackUnlockPageDrawerShown
 * Tracks the event when the UnlockPageDrawer becomes visible
 */
export function trackUnlockPageDrawerShown(props: {
    remainingUnlockCount?: number;
}) {
    track(DashboardPageUnlocksUserEvents.UnlockPageDrawerShown, props);
}

/**
 * trackUnlockPageDrawerUnlockClick
 * Tracks the event when a user clicks the "Unlock this page" button
 */
export function trackUnlockPageDrawerUnlockClick(props: {
    remainingUnlockCount?: number;
}) {
    track(DashboardPageUnlocksUserEvents.UnlockPageDrawerUnlockClick, props);
}

// // // // DashboardUnlockBoundary

/**
 * trackPageUnlockAttempted
 * Tracks the event when the unlock mutation is fired
 */
export function trackPageUnlockAttempted(props: {
    unlockPathname: string;
    organizationId: string;
    remainingUnlockCount: number;
}) {
    track(DashboardPageUnlocksUserEvents.PageUnlockAttempted, props);
}

/**
 * trackPageUnlockSuccess
 * Tracks the event when the unlock mutation succeeds
 * @param {number} props.remainingUnlockCount - The count BEFORE this unlock was spent
 */
export function trackPageUnlockSuccess(props: {
    unlockPathname: string;
    organizationId: string;
    remainingUnlockCount: number;
}) {
    track(DashboardPageUnlocksUserEvents.PageUnlockSuccess, props);
}

/**
 * trackPageUnlockError
 * Tracks the event when the unlock mutation fails
 */
export function trackPageUnlockError(props: {
    unlockPathname: string;
    organizationId: string;
    errorMessage: string;
}) {
    track(DashboardPageUnlocksUserEvents.PageUnlockError, props);
}
